import type { Location } from 'react-router-dom';
import { INTERNAL_ROUTE_SEGMENT } from '@featit/shared/addressing/paths';
import { projectItemPath } from './projectItemUrls';

/**
 * The full page's addresses (I-1205, I-1249, I-1250): plain functions of a path, kept apart from
 * `fullPage.tsx`, whose links, hooks and icon only an item's page needs. `ShellRuntime` answers the
 * iPad's link actions with `itemWindowPath` at boot, and importing it from `fullPage.tsx` put that
 * whole module in the launch tab's entry chunk (T-0559 entry budget, gate 478) — as `fullPageOrigin.ts`
 * did for `readCameFrom` at gate 474.
 */

/**
 * `view=full`: the item's route opens a split (list and panel) without it. A decision's full-size
 * control used to leave it out and landed in the split, which is the "full size does not go full size"
 * half of I-1205.
 */
export const FULL_VIEW_QUERY = 'view=full';

/** This address with `view=full`: the same item, the same filters, drawn as the page. */
export function fullViewHere(
  location: Pick<Location, 'pathname' | 'search'>,
  options: { pathname?: string; focus?: string | null } = {},
): string {
  // In the order the Items list writes its own address (`view` before `focus` and `comment`): a
  // different order is a different address to its sync, which then pushes the reordered one as a second
  // history entry and Back has two steps to take instead of one.
  const params = new URLSearchParams();
  let placed = false;
  for (const [key, value] of new URLSearchParams(location.search)) {
    if (key === 'view') continue;
    // The row being opened is the focus now, not whichever the address named before.
    if (key === 'focus' && options.focus !== undefined) continue;
    if (!placed && (key === 'focus' || key === 'comment')) {
      params.set('view', 'full');
      placed = true;
    }
    params.append(key, value);
  }
  if (!placed) params.set('view', 'full');
  if (options.focus) params.set('focus', options.focus);
  return `${options.pathname ?? location.pathname}?${params.toString()}`;
}

/**
 * I-1249: the item's own page, as "Open in new window" opens it on iPad. Without `view=full` the
 * item's route draws the split, so the new window showed the list and panel the press came from.
 */
export function itemPagePath(workAreaRef: string | null | undefined, itemKey: string): string {
  return projectItemPath(workAreaRef, itemKey, FULL_VIEW_QUERY);
}

/** The surfaces whose `<KEY>` opens an item beside a list: the item's own page is elsewhere. */
const ITEM_BESIDE_A_LIST = new Set(['decisions', 'tasks']);

/**
 * I-1250: where a new iPad window opens a link, the link context menu's Open in New Window. An item's
 * link — on the Items list, Decisions for me or Tasks for me — opens the item's own page; any other
 * link opens as it is. The web answers it with the link's row actions (`linkActionsReply`), so the
 * shell holds no copy of the web's routes.
 */
export function itemWindowPath(path: string): string {
  const url = new URL(path, 'https://featit.invalid');
  const segments = url.pathname.split('/').filter(Boolean).map(decodeURIComponent);
  const app = segments.indexOf(INTERNAL_ROUTE_SEGMENT);
  if (app < 0) return path;
  const [surface, key, ...rest] = segments.slice(app + 1);
  if (!key || key === 'new' || rest.length > 0) return path;
  const workAreaRef = app > 0 ? segments.slice(0, app).join('/') : null;
  if (surface === 'items') return fullViewHere(url);
  if (ITEM_BESIDE_A_LIST.has(surface)) return itemPagePath(workAreaRef, key);
  return path;
}
